import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatIDR, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PaymentBadge } from "@/components/badges";
import { ShoppingBag, Wallet, TrendingUp, Clock, CalendarClock, Coins, HandCoins, Beef } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { motion } from "framer-motion";

const daysUntil = (iso) => {
  if (!iso) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso.slice(0, 10));
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};

const StatCard = ({ icon: Icon, label, value, hint, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay }}
    className="rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]"
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className="flex items-start justify-between">
      <p className="text-sm font-medium text-brand-muted">{label}</p>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-gold/15 text-brand-gold">
        <Icon className="h-[18px] w-[18px]" />
      </div>
    </div>
    <p className="mt-3 font-serif text-3xl font-bold text-brand-espresso">{value}</p>
    {hint && <p className="mt-1 text-xs text-brand-muted">{hint}</p>}
  </motion.div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [fin, setFin] = useState(null);
  const { role } = useAuth();
  const navigate = useNavigate();
  const canFinance = role === "admin" || role === "keuangan";

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!canFinance) return;
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    api.get("/reports/financial", { params: { start: `${y}-${m}-01`, end: `${y}-${m}-31` } })
      .then(({ data }) => setFin(data)).catch(() => {});
  }, [canFinance]);

  const monthly = (data?.monthly_revenue || []).map((m) => ({
    ...m,
    labelMonth: formatDate(`${m.month}-01`).replace(/\d{4}/, "").trim(),
  }));

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 animate-fade-up">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-gold">Ringkasan</p>
        <h1 className="mt-1 font-serif text-4xl font-bold text-brand-espresso">Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={ShoppingBag} label="Total Pesanan" value={data?.total_orders ?? "—"} delay={0.05} />
        <StatCard icon={Clock} label="Pesanan Aktif" value={data?.active_orders ?? "—"} hint="Baru, diproses, dikirim" delay={0.1} />
        <StatCard icon={TrendingUp} label="Total Nilai" value={data ? formatIDR(data.total_revenue) : "—"} hint="Diluar yang dibatalkan" delay={0.15} />
        <StatCard icon={Wallet} label="Sudah Lunas" value={data ? formatIDR(data.paid_revenue) : "—"} delay={0.2} />
      </div>

      {canFinance && fin && (
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3" data-testid="dashboard-finance">
          <StatCard icon={Coins} label="Laba Bersih Bulan Ini" value={formatIDR(fin.summary.net_profit ?? fin.summary.profit)} hint={`Omzet ${formatIDR(fin.summary.omzet)} · Total Biaya ${formatIDR(fin.summary.omzet - (fin.summary.net_profit ?? fin.summary.profit))}`} delay={0.05} />
          <StatCard icon={HandCoins} label="Sisa Tagihan Bulan Ini" value={formatIDR(fin.summary.outstanding)} hint={`Sudah diterima ${formatIDR(fin.summary.received)}`} delay={0.1} />
          <StatCard icon={Beef} label="Jumlah Ekor Domba Bulan Ini" value={`${new Intl.NumberFormat("id-ID").format(fin.summary.lamb_qty || 0)} ekor`} delay={0.15} />
        </div>
      )}

      {data?.upcoming_orders?.length > 0 && (
        <div className="mt-6 rounded-2xl border border-brand-gold/40 bg-brand-gold/5 p-6" data-testid="upcoming-reminders">
          <div className="mb-4 flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-brand-gold" />
            <h2 className="font-serif text-xl font-bold text-brand-espresso">Pengingat Jadwal Aqiqah</h2>
            <span className="rounded-full bg-brand-gold/20 px-2 py-0.5 text-xs font-semibold text-brand-espresso">
              {data.upcoming_orders.length} mendatang
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.upcoming_orders.map((o) => {
              const days = daysUntil(o.schedule_date);
              const cls = days <= 3 ? "bg-red-100 text-red-800" : days <= 7 ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800";
              return (
                <div key={o.id} className="rounded-xl border border-brand-line bg-white p-4 transition-transform duration-200 hover:-translate-y-[1px]"
                  data-testid={`upcoming-${o.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate font-medium text-brand-espresso">{o.customer_name}</p>
                    <StatusBadge status={o.status} />
                  </div>
                  <p className="truncate text-xs text-brand-muted">
                    {o.order_number}{o.aqiqah_name ? ` · ${o.aqiqah_name}` : ""}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-sm text-brand-muted">{formatDate(o.schedule_date)}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>
                      {days === 0 ? "Hari ini" : days === 1 ? "Besok" : `${days} hari lagi`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Revenue chart */}
        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)] lg:col-span-2">
          <h2 className="font-serif text-xl font-bold text-brand-espresso">Pendapatan per Bulan</h2>
          <div className="mt-6 h-64">
            {monthly.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-brand-muted">
                Belum ada data pendapatan.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6DFD5" vertical={false} />
                  <XAxis dataKey="labelMonth" tick={{ fontSize: 12, fill: "#7A6B63" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#7A6B63" }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => `${v / 1000000}jt`} />
                  <Tooltip formatter={(v) => formatIDR(v)} cursor={{ fill: "rgba(212,175,55,0.08)" }} />
                  <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                    {monthly.map((_, i) => (
                      <Cell key={i} fill="#D4AF37" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Status breakdown */}
        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]">
          <h2 className="font-serif text-xl font-bold text-brand-espresso">Status Pesanan</h2>
          <div className="mt-5 space-y-3">
            {["baru", "diproses", "dikirim", "selesai", "dibatalkan"].map((s) => (
              <div key={s} className="flex items-center justify-between">
                <StatusBadge status={s} />
                <span className="font-semibold text-brand-espresso">{data?.by_status?.[s] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent orders */}
      <div className="mt-6 rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif text-xl font-bold text-brand-espresso">Pesanan Terbaru</h2>
          <button onClick={() => navigate("/pesanan")} className="text-sm font-medium text-brand-gold hover:underline" data-testid="view-all-orders">
            Lihat semua
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-brand-muted">
                <th className="pb-3 pr-4 font-semibold">No. Pesanan</th>
                <th className="pb-3 pr-4 font-semibold">Pelanggan</th>
                <th className="pb-3 pr-4 font-semibold">Tanggal</th>
                <th className="pb-3 pr-4 font-semibold">Status</th>
                <th className="pb-3 pr-4 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_orders || []).length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-brand-muted">Belum ada pesanan.</td></tr>
              ) : (
                data.recent_orders.map((o) => (
                  <tr key={o.id} className="border-b border-brand-line/60 hover:bg-brand-surface/50">
                    <td className="py-3 pr-4 font-medium text-brand-espresso">{o.order_number}</td>
                    <td className="py-3 pr-4">{o.customer_name}</td>
                    <td className="py-3 pr-4 text-brand-muted">{formatDate(o.created_at)}</td>
                    <td className="py-3 pr-4"><StatusBadge status={o.status} /></td>
                    <td className="py-3 pr-4 text-right font-semibold text-brand-espresso">{formatIDR(o.total_amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
